// Main entry point for the Zubduq application
import "../scss/main.scss";

// Import UserContextService first for proper initialization order
import UserContextService from "./services/UserContextService.js";

// Import utilities
import { MobileAccessibilityManager, TouchGestureManager } from "./utils/index.js";
import "./utils/modalManager.js";

// Import unified icon system
import IconManager from "./utils/iconSystem.js";

// Import toast utilities to ensure early initialization
import { initializeToastUtils } from "./utils/toastUtils.js";

// Import existing functionality to maintain compatibility
import { ToastManager, getToast, showToast } from "./components/common/Toast.js";
import { UserBarManager, renderUserBar } from "./components/layout/UserBar.js";
import { CSRFManager } from "./utils/csrf.js";

// Import component lifecycle manager for cleanup orchestration
import componentLifecycleManager from "./utils/componentLifecycleManager.js";

// Import navigation component to ensure it's included in the bundle
import "./components/layout/UserNavigation.js";
import UserNavigationManager from "./components/layout/UserNavigation.js";
import MobileNavigation from "./components/layout/mobileNavigation.js";

// Import auth services
import AuthGuard from "./auth/authGuard.js";
import publicAuthService from "./utils/publicAuth.js";

// Import analytics services
import analyticsService from "../services/analyticsService.js";
import cookieConsent from "../services/cookieConsent.js";

// Import version display utility
import "./utils/versionDisplay.js";

// Application initialization
/**
 * Main application initialization
 */
class ZubduqApp {
  constructor() {
    this.initialized = false;
    this.userContextService = null;

    // Component tracking for cleanup orchestration
    this.activeComponents = new Map();
    this.componentInstances = new Map();
    this.routeChangingCleanupHandlers = new Set();
    this.navigationCleanupHandlers = new Set();

    // Background operations state
    this.pausedAutoRefresh = false;
    this.autoRefreshTimer = null;
    this.autoRefreshInterval = 30000; // 30 seconds default

    // Event handlers for cleanup
    this.cleanupEventHandlers = {
      beforeUnload: this.handleBeforeUnload.bind(this),
      pageHide: this.handlePageHide.bind(this),
      visibilityChange: this.handleVisibilityChange.bind(this),
      popState: this.handlePopState.bind(this),
      hashChange: this.handleHashChange.bind(this),
      unload: this.handleUnload.bind(this)
    };

    // Register with lifecycle manager
    componentLifecycleManager.registerComponent('ZubduqApp', this, {
      autoCleanup: true
    });

    console.log('🚀 ZubduqApp initialized with cleanup orchestration');
  }

  /**
   * Initialize the application
   */
  async init() {
    if (this.initialized) return;

    try {
      // Initialize toast utils early to prevent polling
      initializeToastUtils();

      // Setup cleanup orchestration first
      this.setupCleanupOrchestration();

      // Initialize UserContextService first - it's required by other components
      await this.initializeUserContextService();

      // Initialize core components
      await this.initializeCore();

      // Initialize page-specific components
      await this.initializePageComponents();

      this.initialized = true;
      console.log('✅ ZubduqApp: Application fully initialized with cleanup orchestration');
    } catch (error) {
      console.error('Application initialization completed with warnings:', error);
      // Only show critical errors to users
      if (window.showError && error.message.includes('critical')) {
        window.showError('Some features may not be fully initialized: ' + error.message);
      }
    }
  }

  /**
   * Initialize UserContextService and make it globally available
   */
  async initializeUserContextService() {
    try {
      console.log('🔧 Initializing UserContextService...');

      // Create UserContextService instance
      this.userContextService = new UserContextService();

      // Make it globally available for other components
      window.UserContextService = UserContextService;
      window.userContextService = this.userContextService;

      // Wait for service to be ready
      await this.userContextService.waitForReady();

      console.log('✅ UserContextService initialized and ready');
    } catch (error) {
      console.error('❌ Failed to initialize UserContextService:', error);

      // This is critical - show error to user
      if (window.showError) {
        window.showError('critical: User context service failed to initialize');
      }
      throw error;
    }
  }

  /**
   * Initialize core application components
   */
  async initializeCore() {
    // Get current path once for reuse across component initialization
    const currentPath = window.location.pathname;
    const isBookingPage = currentPath.includes('book-appointment.html');
    const isBookingConfirmationPage = currentPath.includes('booking-confirmation.html');

    // Initialize CSRF protection
    await CSRFManager.init();

    // Ensure analytics is initialized with config
    // Ensure analytics service is properly initialized
    const analyticsReady = await analyticsService.ensureInitialized();

    // Cookie consent service auto-initializes itself

    // Setup global analytics tracking
    this.setupAnalyticsTracking();

    // Initialize UserBar if needed - now depends on UserContextService
    // Skip on book-appointment and booking-confirmation pages for a cleaner booking experience
    if (document.getElementById('userbar-container') && !isBookingPage && !isBookingConfirmationPage) {
      console.log('🧭 Initializing UserBar with UserContextService dependency...');

      // Use singleton pattern - check if already exists
      if (!UserBarManager.instance) {
        console.log('🧭 Creating UserBarManager from main.js');
        await UserBarManager.init();

        // Register with cleanup orchestrator - CRITICAL for layout stability
        this.registerComponent('UserBarManager', UserBarManager, {
          critical: true,
          cleanupOnNavigation: false
        });

        console.log('🧭 UserBar initialized from main.js');
      } else {
        console.log('🚫 UserBarManager already exists, skipping main.js initialization');

        // Register existing instance - CRITICAL for layout stability
        this.registerComponent('UserBarManager', UserBarManager.instance, {
          critical: true,
          cleanupOnNavigation: false
        });
      }
    } else if (isBookingPage || isBookingConfirmationPage) {
      console.log('📄 Skipping UserBar on booking pages');
    }

    // Initialize Toast system (getToast() returns initialized singleton)
    const toastManager = getToast();
    this.registerComponent('ToastManager', toastManager, {
      critical: true,
      cleanupOnNavigation: false
    });

    // Initialize touch gesture manager for enhanced mobile experience
    TouchGestureManager.init();
    this.registerComponent('TouchGestureManager', TouchGestureManager, {
      critical: false,
      cleanupOnNavigation: false
    });

    // Initialize mobile accessibility manager
    MobileAccessibilityManager.init();
    this.registerComponent('MobileAccessibilityManager', MobileAccessibilityManager, {
      critical: false,
      cleanupOnNavigation: false
    });

    // Initialize Icon System for global icon injection
    console.log('🎨 Initializing IconManager...');
    IconManager.initialize();
    this.registerComponent('IconManager', IconManager, {
      critical: true,
      cleanupOnNavigation: false
    });

    // Make IconManager globally available
    window.IconManager = IconManager;

    // Initialize UserNavigation for breadcrumbs and flyouts
    console.log('🧭 Initializing UserNavigation...');

    // Use singleton pattern - check if already exists
    if (!UserNavigationManager.instance) {
      console.log('🧭 Creating UserNavigationManager from main.js');
      const userNav = new UserNavigationManager();

      // Register with cleanup orchestrator - CRITICAL for layout stability
      this.registerComponent('UserNavigationManager', userNav, {
        critical: true,
        cleanupOnNavigation: false
      });

      console.log('🧭 UserNavigation initialized from main.js');
    } else {
      console.log('🚫 UserNavigationManager already exists, skipping main.js initialization');

      // Register existing instance - CRITICAL for layout stability
      this.registerComponent('UserNavigationManager', UserNavigationManager.instance, {
        critical: true,
        cleanupOnNavigation: false
      });
    }

    // Initialize Mobile Navigation for mobile flyout triggers
    // Skip on booking pages to avoid cluttering the booking experience
    if (!isBookingPage && !isBookingConfirmationPage) {
      console.log('📱 Initializing Mobile Navigation...');
      const mobileNav = new MobileNavigation();

      // Register with cleanup orchestrator - CRITICAL for layout stability
      this.registerComponent('MobileNavigation', mobileNav, {
        critical: true,
        cleanupOnNavigation: false
      });

      // Make mobile navigation available globally for UserNavigation integration
      window.mobileNavigation = mobileNav;

      console.log('📱 Mobile Navigation initialized');
    } else {
      console.log('📄 Skipping Mobile Navigation on booking pages');
    }
  }

  /**
   * Initialize page-specific components
   */
  async initializePageComponents() {
    // Page-specific initialization will be handled by individual page modules
    const currentPath = window.location.pathname;

    // Home page
    if (currentPath === '/' || currentPath === '/index.html') {
      const { default: homePageManager } = await import('./pages/home.js');
      await homePageManager.init();

      // Register with cleanup orchestrator
      this.registerComponent('HomePageManager', homePageManager, {
        critical: false,
        cleanupOnNavigation: true
      });
    }

    // Public authentication pages
    if (currentPath.includes('/auth/')) {
      // Admin/User Login page
      if (currentPath.endsWith('login.html') && !currentPath.includes('public-login.html')) {
        const { default: loginComponent } = await import('./components/auth/LoginComponent.js');
        await loginComponent.init();

        // Register with cleanup orchestrator
        this.registerComponent('LoginComponent', loginComponent, {
          critical: false,
          cleanupOnNavigation: true
        });
      }

      // Public Login page
      if (currentPath.endsWith('public-login.html')) {
        const { default: publicLoginComponent } = await import('./components/auth/PublicLoginComponent.js');
        await publicLoginComponent.init();

        // Register with cleanup orchestrator
        this.registerComponent('PublicLoginComponent', publicLoginComponent, {
          critical: false,
          cleanupOnNavigation: true
        });

        // Initialize Social Login Component
        const { default: SocialLoginComponent } = await import('./components/auth/SocialLoginComponent.js');
        const socialLoginComponent = new SocialLoginComponent('#social-login-container');

        // Register with cleanup orchestrator
        this.registerComponent('SocialLoginComponent', socialLoginComponent, {
          critical: false,
          cleanupOnNavigation: true
        });
      }

      // Admin/User Registration page
      if (currentPath.endsWith('register.html') && !currentPath.includes('public-register.html')) {
        const { default: RegisterComponent } = await import('./components/auth/RegisterComponent.js');
        const registerComponent = new RegisterComponent();

        // Register with cleanup orchestrator
        this.registerComponent('RegisterComponent', registerComponent, {
          critical: false,
          cleanupOnNavigation: true
        });
      }

      // Public Registration page
      if (currentPath.endsWith('public-register.html')) {
        const { default: publicRegistrationComponent } = await import('./components/auth/PublicRegistrationComponent.js');
        await publicRegistrationComponent.init();

        // Register with cleanup orchestrator
        this.registerComponent('PublicRegistrationComponent', publicRegistrationComponent, {
          critical: false,
          cleanupOnNavigation: true
        });

        // Initialize Social Login Component
        const { default: SocialLoginComponent } = await import('./components/auth/SocialLoginComponent.js');
        const socialLoginComponent = new SocialLoginComponent('#social-login-container');

        // Register with cleanup orchestrator
        this.registerComponent('SocialLoginComponent', socialLoginComponent, {
          critical: false,
          cleanupOnNavigation: true
        });
      }

      // Email Verification page (admin/employee only)
      if (currentPath.endsWith('verify-email.html')) {
        const { default: EmailVerificationManager } = await import('./pages/auth/EmailVerificationManager.js');
        const emailVerificationManager = new EmailVerificationManager();
        await emailVerificationManager.init();

        // Register with cleanup orchestrator
        this.registerComponent('EmailVerificationManager', emailVerificationManager, {
          critical: false,
          cleanupOnNavigation: true
        });
      }

      // Password Reset pages (forgot-password and reset-password)
      if (currentPath.includes('forgot-password.html') || currentPath.includes('reset-password.html')) {
        const { default: PasswordResetManager } = await import('./pages/auth/PasswordResetManager.js');
        const passwordResetManager = new PasswordResetManager();
        await passwordResetManager.init();

        // Register with cleanup orchestrator
        this.registerComponent('PasswordResetManager', passwordResetManager, {
          critical: false,
          cleanupOnNavigation: true
        });
      }
    }

    // Public booking pages
    if (currentPath.includes('/booking/')) {
      console.log('🔍 Booking path detected:', currentPath);
      // Apply dynamic landing page theme to booking pages before component initialization
      try {
        if (currentPath.endsWith('book-appointment.html') || currentPath.endsWith('booking-confirmation.html') || currentPath.endsWith('join-queue.html')) {
          const { default: bookingThemeBootstrap } = await import('./pages/booking/bookingThemeBootstrap.js');
          await bookingThemeBootstrap.init();
          console.log('🎨 Booking theme bootstrap applied');
        }
      } catch (themeError) {
        console.warn('Booking theme bootstrap error:', themeError.message);
      }

      // Book Appointment page (time-slot booking only)
      if (currentPath.endsWith('book-appointment.html')) {
        console.log('🚀 Loading BookingComponent...');
        const { default: BookingComponent } = await import('./components/booking/BookingComponent.js');
        const bookingComponent = new BookingComponent();
        await bookingComponent.init();

        // Register with cleanup orchestrator
        this.registerComponent('BookingComponent', bookingComponent, {
          critical: false,
          cleanupOnNavigation: true
        });
        console.log('✅ BookingComponent loaded and initialized');
      }

      // Join Queue page (queue-based booking)
      if (currentPath.endsWith('join-queue.html')) {
        console.log('🎫 Loading JoinQueueComponent...');
        const { default: JoinQueueComponent } = await import('./components/booking/JoinQueueComponent.js');
        const joinQueueComponent = new JoinQueueComponent();
        await joinQueueComponent.init();

        // Register with cleanup orchestrator
        this.registerComponent('JoinQueueComponent', joinQueueComponent, {
          critical: false,
          cleanupOnNavigation: true
        });
        console.log('✅ JoinQueueComponent loaded and initialized');
      }

      // Booking Confirmation page
      if (currentPath.endsWith('booking-confirmation.html')) {
        const { default: BookingConfirmationComponent } = await import('./components/booking/BookingConfirmationComponent.js');
        const bookingConfirmationComponent = new BookingConfirmationComponent();
        await bookingConfirmationComponent.init();

        // Register with cleanup orchestrator
        this.registerComponent('BookingConfirmationComponent', bookingConfirmationComponent, {
          critical: false,
          cleanupOnNavigation: true
        });
      }
    } else {
      console.log('📍 Current path does not include /booking/:', currentPath);
    }

    // Admin pages
    if (currentPath.includes('/admin/')) {
      // Check admin authentication before loading any admin modules
      const isAuthorized = await AuthGuard.initForPage('admin', (user) => {
        console.log('Admin access granted:', user.name);
      });

      if (!isAuthorized) return; // Auth guard will handle redirect

      // Get the exact page name for precise matching (strip query parameters)
      const pageName = currentPath.split('/').pop()?.split('?')[0] || '';

      // Organization Management page
      if (pageName === 'organization-management.html') {
        const { default: organizationManager } = await import('./pages/admin/OrganizationManagement.js');
        await organizationManager.init();

        // Register with cleanup orchestrator
        this.registerComponent('OrganizationManager', organizationManager, {
          critical: false,
          cleanupOnNavigation: true
        });
      }

      // Leads Management page
      else if (pageName === 'leads-management.html') {
        const leadsModule = await import('./pages/admin/LeadsManagement.js');
        const leadsManager = leadsModule.default;
        await leadsManager.init();

        // Register with cleanup orchestrator
        this.registerComponent('LeadsManager', leadsManager, {
          critical: false,
          cleanupOnNavigation: true
        });

        // Also setup modal events if they exist
        if (typeof leadsModule.setupGlobalModalEvents === 'function') {
          leadsModule.setupGlobalModalEvents();
        }
      }

      // User Management page
      else if (pageName === 'user-management.html') {
        const { default: userManager } = await import('./pages/admin/UserManagement.js');
        await userManager.init();

        // Register with cleanup orchestrator
        this.registerComponent('UserManager', userManager, {
          critical: false,
          cleanupOnNavigation: true
        });
      }

      // Employee-Service Management page
      if (pageName === 'employee-service-management.html') {
        const { default: employeeServiceManager } = await import('./pages/admin/EmployeeServiceManagement.js');
        await employeeServiceManager.init();

        // Register with cleanup orchestrator
        this.registerComponent('EmployeeServiceManager', employeeServiceManager, {
          critical: false,
          cleanupOnNavigation: true
        });
      }
      // Service Management page
      else if (pageName === 'service-management.html') {
        const { default: serviceManager } = await import('./pages/admin/ServiceManagement.js');
        await serviceManager.init();

        // Register with cleanup orchestrator
        this.registerComponent('ServiceManager', serviceManager, {
          critical: false,
          cleanupOnNavigation: true
        });
      }

      // Product Management page
      else if (pageName === 'product-management.html') {
        const { default: productManager } = await import('./pages/admin/ProductManagement.js');
        await productManager.init();

        // Register with cleanup orchestrator
        this.registerComponent('ProductManager', productManager, {
          critical: false,
          cleanupOnNavigation: true
        });
      }

      // Availability Management page
      else if (pageName === 'availability-management.html') {
        const { default: availabilityManager } = await import('./pages/admin/AvailabilityManagement.js');
        await availabilityManager.init();

        // Register with cleanup orchestrator
        this.registerComponent('AvailabilityManager', availabilityManager, {
          critical: false,
          cleanupOnNavigation: true
        });
      }

      // Appointments Management page
      else if (pageName === 'appointments-management.html') {
        const { default: appointmentsManager } = await import('./pages/admin/AppointmentsManagement.js');
        await appointmentsManager.init();

        // Register with cleanup orchestrator
        this.registerComponent('AppointmentsManager', appointmentsManager, {
          critical: false,
          cleanupOnNavigation: true
        });
      }

      // Landing Page Builder
      if (pageName === 'landing-page-builder.html') {
        const { default: landingPageBuilder } = await import('./pages/admin/LandingPageBuilder.js');
        await landingPageBuilder.init();

        // Register with cleanup orchestrator
        this.registerComponent('LandingPageBuilder', landingPageBuilder, {
          critical: false,
          cleanupOnNavigation: true
        });
      }

      // Organization Settings
      if (pageName === 'organization-settings.html') {
        const { OrganizationSettings } = await import('./pages/admin/OrganizationSettings.js');
        const organizationSettings = new OrganizationSettings();
        await organizationSettings.init();

        // Register with cleanup orchestrator
        this.registerComponent('OrganizationSettings', organizationSettings, {
          critical: false,
          cleanupOnNavigation: true
        });
      }

      // Admin Queue Management page
      if (pageName === 'queue-management.html' && currentPath.includes('/admin/')) {
        const { default: AdminQueueManagementManager } = await import('./pages/admin/queueManagement.js');
        const adminQueueManagement = new AdminQueueManagementManager();
        await adminQueueManagement.init();

        // Make available globally for inline handlers
        window.adminQueueManager = adminQueueManagement;

        // Register with cleanup orchestrator
        this.registerComponent('AdminQueueManagementManager', adminQueueManagement, {
          critical: false,
          cleanupOnNavigation: true
        });
        console.log('✅ Admin Queue Management initialized');
      }

      // Admin Dashboard
      if (currentPath.includes('admin-dashboard.html')) {
        const { default: adminDashboard } = await import('./pages/admin/AdminDashboard.js');
        await adminDashboard.init();

        // Register with cleanup orchestrator
        this.registerComponent('AdminDashboard', adminDashboard, {
          hasDestroy: true,
          critical: false,
          cleanupOnNavigation: true
        });
      }
    }

    // Employee pages
    if (currentPath.includes('/employee/')) {
      // Check employee authentication before loading any employee modules
      const isAuthorized = await AuthGuard.initForPage('employee', (user) => {
        console.log('Employee access granted:', user.name);
      });

      if (!isAuthorized) return; // Auth guard will handle redirect

      // Employee Dashboard page
      if (currentPath.includes('employee-dashboard.html')) {
        const { default: employeeDashboardManager } = await import('./pages/employee/employeeDashboard.js');

        // Register with cleanup orchestrator
        this.registerComponent('EmployeeDashboardManager', employeeDashboardManager, {
          critical: false,
          cleanupOnNavigation: true
        });

        // The module will auto-initialize with DOMContentLoaded
      }

      // Employee Leads page
      if (currentPath.includes('employee-leads.html')) {
        const { default: userLeadsManager } = await import('./pages/employee/leads.js');
        await userLeadsManager.init();

        // Register with cleanup orchestrator
        this.registerComponent('UserLeadsManager', userLeadsManager, {
          critical: false,
          cleanupOnNavigation: true
        });
      }

      // Employee Appointments page
      if (currentPath.includes('employee-appointments.html')) {
        // Import and initialize employee appointments module if it exists
        try {
          const { default: employeeAppointmentsManager } = await import('./pages/employee/employeeAppointments.js');
          await employeeAppointmentsManager.init();

          // Register with cleanup orchestrator
          this.registerComponent('EmployeeAppointmentsManager', employeeAppointmentsManager, {
            critical: false,
            cleanupOnNavigation: true
          });
        } catch (error) {
          console.log('Employee appointments module not found, skipping...');
        }
      }

      // Queue Management page
      if (currentPath.includes('queue-management.html')) {
        try {
          const { QueueManagementComponent } = await import('./components/employee/QueueManagementComponent.js');
          const queueManagement = new QueueManagementComponent();
          await queueManagement.init();

          // Register with cleanup orchestrator
          this.registerComponent('QueueManagementComponent', queueManagement, {
            critical: false,
            cleanupOnNavigation: true,
            hasDestroy: true
          });
          console.log('✅ Queue Management Component (with Socket.IO) initialized');
        } catch (error) {
          console.error('Queue management component error:', error);
        }
      }
    }

    // Customer pages
    if (currentPath.includes('/customer/')) {
      // Check customer authentication before loading any customer modules
      // Customer Dashboard - Allow public users (Google Sign-In)
      const isAuthorized = await AuthGuard.initForPage('public', (user) => {
        console.log('Public user access granted:', user.name);
      });

      if (!isAuthorized) return; // Auth guard will handle redirect

      // Customer Dashboard page
      if (currentPath.includes('customer-dashboard.html')) {
        const { default: customerDashboardManager } = await import('./pages/customer/customerDashboard.js');

        // Register with cleanup orchestrator
        this.registerComponent('CustomerDashboardManager', customerDashboardManager, {
          critical: false,
          cleanupOnNavigation: true
        });

        // The module will auto-initialize with DOMContentLoaded
      }

      // Customer Bookings page
      if (currentPath.includes('bookings.html')) {
        // Import and initialize customer bookings module
        try {
          const { default: customerBookingsManager } = await import('./pages/customer/bookings.js');

          // Register with cleanup orchestrator
          this.registerComponent('CustomerBookingsManager', customerBookingsManager, {
            critical: false,
            cleanupOnNavigation: true
          });

          // The module auto-initializes with DOMContentLoaded
        } catch (error) {
          console.log('Customer bookings module initialization handled internally');
        }
      }

      // Customer Book Appointment page
      if (currentPath.includes('book-appointment.html')) {
        // Import and initialize customer booking module
        try {
          const { default: customerBookingManager } = await import('./pages/customer/customerBooking.js');
          await customerBookingManager.init();

          // Register with cleanup orchestrator
          this.registerComponent('CustomerBookingManager', customerBookingManager, {
            critical: false,
            cleanupOnNavigation: true
          });
        } catch (error) {
          console.log('Customer booking module error:', error.message);
        }
      }
    }

    // Employee pages
    if (currentPath.includes('/employee/')) {
      console.log('📋 Loading Employee page components...');
    }
  }

  /**
   * Setup analytics tracking for common interactions
   */
  setupAnalyticsTracking() {
    // Store handlers for cleanup later
    this.submitHandler = (event) => {
      const form = event.target;
      const formName = form.id || form.className || 'unknown_form';

      // Track form submission
      analyticsService.trackFormSubmission(formName);

      // Track specific form types
      if (form.classList.contains('login-form')) {
        analyticsService.trackAuthEvent('login_attempt');
      } else if (form.classList.contains('register-form')) {
        analyticsService.trackAuthEvent('register_attempt');
      } else if (form.id === 'public-login-form') {
        analyticsService.trackAuthEvent('public_login_attempt');
      } else if (form.id === 'public-register-form') {
        analyticsService.trackAuthEvent('public_register_attempt');
      } else if (form.classList.contains('lead-form')) {
        analyticsService.trackCRMEvent('lead_form_submit', 'lead');
      }
    };

    // Track button clicks with data-track attribute
    this.clickHandler = (event) => {
      const element = event.target.closest('button[data-track], a[data-track]');
      if (!element) return;

      const action = element.dataset.track;
      const category = element.dataset.trackCategory || 'button_click';
      const label = element.textContent.trim() || element.dataset.trackLabel || '';

      analyticsService.trackUserInteraction(action, category, label);
    };

    // Track navigation between pages
    this.navigationClickHandler = (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (href && (href.startsWith('/') || href.includes(window.location.hostname))) {
        analyticsService.trackNavigation(window.location.pathname, href, 'click');
      }
    };

    // Track session duration
    this.beforeUnloadHandler = () => {
      const sessionStart = sessionStorage.getItem('session_start');
      if (sessionStart) {
        const sessionDuration = Math.round((Date.now() - parseInt(sessionStart)) / 1000);
        analyticsService.trackEvent('session_duration', {
          value: sessionDuration,
          event_category: 'engagement'
        });
      }
    };

    // Add event listeners
    document.addEventListener('submit', this.submitHandler);
    document.addEventListener('click', this.clickHandler);
    document.addEventListener('click', this.navigationClickHandler);
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    // Set session start time
    if (!sessionStorage.getItem('session_start')) {
      sessionStorage.setItem('session_start', Date.now().toString());
    }

    // Track page visibility changes (throttled for localhost)
    let lastVisibilityChange = 0;
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const throttleMs = isLocalhost ? 5000 : 1000; // 5 seconds on localhost, 1 second in production

    this.visibilityChangeHandler = () => {
      const now = Date.now();
      if (now - lastVisibilityChange < throttleMs) return; // Throttle frequent visibility changes
      lastVisibilityChange = now;

      if (document.visibilityState === 'hidden') {
        analyticsService.trackEvent('page_hidden', {
          event_category: 'engagement'
        });
      } else if (document.visibilityState === 'visible') {
        analyticsService.trackEvent('page_visible', {
          event_category: 'engagement'
        });
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  /**
   * Setup comprehensive cleanup orchestration system
   */
  setupCleanupOrchestration() {
    console.log('🔧 Setting up cleanup orchestration...');

    // Register all cleanup event handlers
    window.addEventListener('beforeunload', this.cleanupEventHandlers.beforeUnload);
    window.addEventListener('pagehide', this.cleanupEventHandlers.pageHide);
    document.addEventListener('visibilitychange', this.cleanupEventHandlers.visibilityChange);
    window.addEventListener('popstate', this.cleanupEventHandlers.popState);
    window.addEventListener('hashchange', this.cleanupEventHandlers.hashChange);
    window.addEventListener('unload', this.cleanupEventHandlers.unload);

    // Setup mutation observer for DOM changes that might indicate navigation
    if (window.MutationObserver) {
      this.setupNavigationObserver();
    }

    // Setup route change detection
    this.setupRouteChangeDetection();

    // Make cleanup orchestrator globally available
    window.cleanupOrchestrator = this;

    console.log('✅ Cleanup orchestration setup completed');
  }

  /**
   * Setup navigation observer for DOM-based navigation detection
   */
  setupNavigationObserver() {
    this.navigationObserver = new MutationObserver((mutations) => {
      let navigationDetected = false;

      mutations.forEach((mutation) => {
        // Check for significant DOM changes that might indicate navigation
        if (mutation.type === 'childList') {
          const addedNodes = Array.from(mutation.addedNodes);
          const removedNodes = Array.from(mutation.removedNodes);

          // Look for significant content changes
          const significantChange = addedNodes.some(node =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.tagName === 'MAIN' || node.classList?.contains('page-content'))
          ) || removedNodes.some(node =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.tagName === 'MAIN' || node.classList?.contains('page-content'))
          );

          if (significantChange) {
            navigationDetected = true;
          }
        }
      });

      if (navigationDetected) {
        this.handleNavigationChange('dom-mutation');
      }
    });

    // Observe the main content area
    const mainContent = document.body;
    if (mainContent) {
      this.navigationObserver.observe(mainContent, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Setup route change detection using multiple strategies
   */
  setupRouteChangeDetection() {
    // Store original methods for restoration
    this.originalPushState = window.history.pushState;
    this.originalReplaceState = window.history.replaceState;

    // Override history methods to detect programmatic navigation
    window.history.pushState = (...args) => {
      this.handleNavigationChange('pushstate');
      return this.originalPushState.apply(window.history, args);
    };

    window.history.replaceState = (...args) => {
      this.handleNavigationChange('replacestate');
      return this.originalReplaceState.apply(window.history, args);
    };
  }

  /**
   * Register a component for cleanup orchestration
   */
  registerComponent(name, instance, options = {}) {
    console.log(`🔧 Registering component for cleanup: ${name}`);

    this.activeComponents.set(name, {
      instance,
      hasCleanup: typeof instance.cleanup === 'function',
      hasDestroy: typeof instance.destroy === 'function',
      cleanupOnNavigation: options.cleanupOnNavigation !== false,
      critical: options.critical === true,
      ...options
    });

    this.componentInstances.set(name, instance);

    // Also register with lifecycle manager
    componentLifecycleManager.registerComponent(name, instance, options);

    console.log(`✅ Component registered: ${name}`);
    return true;
  }

  /**
   * Unregister a component from cleanup orchestration
   */
  unregisterComponent(name) {
    console.log(`🗑️ Unregistering component: ${name}`);

    const success = this.activeComponents.delete(name) && this.componentInstances.delete(name);

    // Also unregister from lifecycle manager
    componentLifecycleManager.unregisterComponent(name);

    console.log(`${success ? '✅' : '⚠️'} Component unregistered: ${name}`);
    return success;
  }

  /**
   * Add cleanup handler for route changes
   */
  addRouteChangeCleanupHandler(handler, description = '') {
    console.log(`🔗 Adding route change cleanup handler: ${description}`);
    this.routeChangingCleanupHandlers.add(handler);
  }

  /**
   * Add cleanup handler for navigation
   */
  addNavigationCleanupHandler(handler, description = '') {
    console.log(`🧭 Adding navigation cleanup handler: ${description}`);
    this.navigationCleanupHandlers.add(handler);
  }

  /**
   * Handle before unload event - critical cleanup
   */
  handleBeforeUnload(event) {
    console.log('🚨 ZubduqApp: beforeunload triggered - performing critical cleanup');

    try {
      this.performMasterCleanup('beforeunload');

      // Allow other beforeunload handlers to run
      return undefined;
    } catch (error) {
      console.error('❌ Error during beforeunload cleanup:', error);
      return undefined;
    }
  }

  /**
   * Handle page hide event - comprehensive cleanup
   */
  handlePageHide(event) {
    console.log('🚨 ZubduqApp: pagehide triggered - performing comprehensive cleanup');

    try {
      this.performMasterCleanup('pagehide');
    } catch (error) {
      console.error('❌ Error during pagehide cleanup:', error);
    }
  }

  /**
   * Handle visibility change - smart pause/resume instead of cleanup
   */
  handleVisibilityChange(event) {
    if (document.visibilityState === 'hidden') {
      console.log('👁️ ZubduqApp: Page hidden - pausing background operations');
      try {
        this.pauseBackgroundOperations('visibility-hidden');
      } catch (error) {
        console.error('❌ Error during visibility pause:', error);
      }
    } else if (document.visibilityState === 'visible') {
      console.log('👁️ ZubduqApp: Page visible - resuming and verifying components');
      try {
        this.resumeAndVerifyComponents('visibility-visible');
      } catch (error) {
        console.error('❌ Error during visibility resume:', error);
      }
    }
  }

  /**
   * Pause background operations without destroying components
   */
  pauseBackgroundOperations(reason) {
    console.log(`⏸️ ZubduqApp: Pausing background operations (${reason})`);

    try {
      // Pause auto-refresh timers but keep event listeners
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.pausedAutoRefresh = true;
        console.log('✅ Auto-refresh timer paused');
      }

      // Pause any polling operations
      if (window.pollingManager && typeof window.pollingManager.pauseAll === 'function') {
        window.pollingManager.pauseAll();
        console.log('✅ Polling operations paused');
      }

      // Pause animations but keep components intact
      document.body.classList.add('app-paused');

    } catch (error) {
      console.error('❌ Error pausing background operations:', error);
    }
  }

  /**
   * Resume operations and verify component integrity
   */
  resumeAndVerifyComponents(reason) {
    console.log(`▶️ ZubduqApp: Resuming operations and verifying components (${reason})`);

    try {
      this.resumeBackgroundOperations();
      this.verifyComponentHealth();
    } catch (error) {
      console.error('❌ Error during resume and verification:', error);
    }
  }

  /**
   * Resume background operations
   */
  resumeBackgroundOperations() {
    console.log('▶️ ZubduqApp: Resuming background operations');

    try {
      // Resume auto-refresh if it was paused
      if (this.pausedAutoRefresh && this.autoRefreshInterval) {
        this.autoRefreshTimer = setInterval(() => {
          this.refreshCurrentPage();
        }, this.autoRefreshInterval);
        this.pausedAutoRefresh = false;
        console.log('✅ Auto-refresh timer resumed');
      }

      // Resume polling operations
      if (window.pollingManager && typeof window.pollingManager.resumeAll === 'function') {
        window.pollingManager.resumeAll();
        console.log('✅ Polling operations resumed');
      }

      // Remove pause state
      document.body.classList.remove('app-paused');

    } catch (error) {
      console.error('❌ Error resuming background operations:', error);
    }
  }

  /**
   * Verify critical component health and reinitialize if needed
   */
  verifyComponentHealth() {
    console.log('🏥 ZubduqApp: Verifying component health');

    try {
      const criticalComponents = ['ModalManager', 'UserNavigation'];
      let verifiedCount = 0;
      let restoredCount = 0;

      criticalComponents.forEach(name => {
        try {
          // Check if modal manager still responds
          if (name === 'ModalManager' && window.modalManager) {
            if (!this.testModalManagerHealth()) {
              console.log(`🔧 Restoring ModalManager functionality`);
              window.modalManager.reinitialize?.();
              restoredCount++;
            } else {
              verifiedCount++;
            }
          }

          // Check if navigation still works
          if (name === 'UserNavigation') {
            if (!this.testNavigationHealth()) {
              console.log(`🔧 Restoring UserNavigation functionality`);
              this.reinitializeNavigation();
              restoredCount++;
            } else {
              verifiedCount++;
            }
          }
        } catch (error) {
          console.error(`❌ Error verifying ${name}:`, error);
        }
      });

      console.log(`✅ Component health check: ${verifiedCount} verified, ${restoredCount} restored`);

    } catch (error) {
      console.error('❌ Error during component health verification:', error);
    }
  }

  /**
   * Test if modal manager is still functional
   */
  testModalManagerHealth() {
    try {
      return !!(window.modalManager &&
        typeof window.modalManager.showModal === 'function' &&
        document.querySelector('[data-modal-trigger]'));
    } catch (error) {
      return false;
    }
  }

  /**
   * Test if navigation is still functional
   */
  testNavigationHealth() {
    try {
      const navTriggers = document.querySelectorAll('[data-flyout], .nav-trigger');
      return navTriggers.length > 0 &&
        Array.from(navTriggers).some(trigger => trigger._flyoutEventsAttached);
    } catch (error) {
      return false;
    }
  }

  /**
   * Reinitialize navigation components
   */
  reinitializeNavigation() {
    try {
      if (window.userNavigationManager && typeof window.userNavigationManager.reinitialize === 'function') {
        window.userNavigationManager.reinitialize();
        console.log('✅ UserNavigation reinitialized');
      }
    } catch (error) {
      console.error('❌ Error reinitializing navigation:', error);
    }
  }

  /**
   * Handle popstate event - navigation cleanup
   */
  handlePopState(event) {
    console.log('🔙 ZubduqApp: popstate triggered - handling navigation cleanup');
    this.handleNavigationChange('popstate');
  }

  /**
   * Handle hash change - lightweight cleanup
   */
  handleHashChange(event) {
    console.log('🔗 ZubduqApp: hashchange triggered - handling hash navigation');
    this.handleNavigationChange('hashchange');
  }

  /**
   * Handle unload event - final cleanup
   */
  handleUnload(event) {
    console.log('🚨 ZubduqApp: unload triggered - performing final cleanup');

    try {
      this.performMasterCleanup('unload');
    } catch (error) {
      console.error('❌ Error during unload cleanup:', error);
    }
  }

  /**
   * Handle navigation change events
   */
  handleNavigationChange(type) {
    console.log(`🧭 ZubduqApp: Navigation change detected (${type}) - running navigation cleanup`);

    try {
      // Run navigation-specific cleanup handlers
      this.navigationCleanupHandlers.forEach((handler) => {
        try {
          handler(type);
        } catch (error) {
          console.error('❌ Error in navigation cleanup handler:', error);
        }
      });

      // Run route change cleanup handlers
      this.routeChangingCleanupHandlers.forEach((handler) => {
        try {
          handler(type);
        } catch (error) {
          console.error('❌ Error in route change cleanup handler:', error);
        }
      });

      // Cleanup components that should cleanup on navigation
      this.cleanupNavigationComponents();

    } catch (error) {
      console.error('❌ Error during navigation change handling:', error);
    }
  }

  /**
   * Cleanup components that should cleanup on navigation
   */
  cleanupNavigationComponents() {
    console.log('🧹 Cleaning up navigation-sensitive components...');

    let cleanedCount = 0;
    this.activeComponents.forEach((config, name) => {
      if (config.cleanupOnNavigation && !config.critical) {
        try {
          this.cleanupSingleComponent(name, config);
          cleanedCount++;
        } catch (error) {
          console.error(`❌ Error cleaning navigation component ${name}:`, error);
        }
      }
    });

    console.log(`✅ Cleaned ${cleanedCount} navigation-sensitive components`);
  }

  /**
   * Perform master cleanup of all registered components
   */
  performMasterCleanup(trigger = 'manual') {
    console.log(`🧹 ZubduqApp: Starting master cleanup (trigger: ${trigger})`);

    const startTime = performance.now();
    let cleanedCount = 0;
    let errorCount = 0;

    try {
      // 1. Cleanup all registered components
      const componentNames = Array.from(this.activeComponents.keys());
      console.log(`📋 Found ${componentNames.length} components to cleanup`);

      componentNames.forEach(name => {
        const config = this.activeComponents.get(name);
        try {
          this.cleanupSingleComponent(name, config);
          cleanedCount++;
        } catch (error) {
          console.error(`❌ Error cleaning component ${name}:`, error);
          errorCount++;
        }
      });

      // 2. Run global cleanup
      this.cleanup();

      // 3. Cleanup lifecycle manager
      componentLifecycleManager.cleanupAll();

      // 4. Clear our own data structures
      this.clearDataStructures();

      const endTime = performance.now();
      console.log(`✅ ZubduqApp: Master cleanup completed in ${(endTime - startTime).toFixed(2)}ms`);
      console.log(`📊 Stats: ${cleanedCount} components cleaned, ${errorCount} errors`);

    } catch (error) {
      console.error('❌ Critical error during master cleanup:', error);
    }
  }

  /**
   * Cleanup a single component
   */
  cleanupSingleComponent(name, config) {
    console.log(`🧹 Cleaning up component: ${name}`);

    if (config.hasCleanup) {
      config.instance.cleanup();
      console.log(`✅ Called cleanup() for ${name}`);
    } else if (config.hasDestroy) {
      config.instance.destroy();
      console.log(`✅ Called destroy() for ${name}`);
    } else {
      console.log(`⚠️ No cleanup method found for ${name}`);
    }
  }

  /**
   * Clear internal data structures
   */
  clearDataStructures() {
    console.log('🧹 Clearing internal cleanup orchestrator data structures...');

    this.activeComponents.clear();
    this.componentInstances.clear();
    this.routeChangingCleanupHandlers.clear();
    this.navigationCleanupHandlers.clear();

    // Disconnect navigation observer
    if (this.navigationObserver) {
      this.navigationObserver.disconnect();
      this.navigationObserver = null;
    }

    // Restore original history methods
    if (this.originalPushState) {
      window.history.pushState = this.originalPushState;
    }
    if (this.originalReplaceState) {
      window.history.replaceState = this.originalReplaceState;
    }

    console.log('✅ Internal data structures cleared');
  }

  /**
   * Get cleanup orchestrator status
   */
  getCleanupStatus() {
    return {
      activeComponents: Array.from(this.activeComponents.keys()),
      componentCount: this.activeComponents.size,
      routeChangeHandlers: this.routeChangingCleanupHandlers.size,
      navigationHandlers: this.navigationCleanupHandlers.size,
      lifecycleManagerStatus: componentLifecycleManager.getComponentStatus()
    };
  }

  /**
   * Cleanup global event listeners and resources
   */
  cleanup() {
    console.log('🧹 ZubduqApp: Cleaning up global resources');

    // Remove analytics event listeners
    if (this.submitHandler) {
      document.removeEventListener('submit', this.submitHandler);
    }
    if (this.clickHandler) {
      document.removeEventListener('click', this.clickHandler);
    }
    if (this.navigationClickHandler) {
      document.removeEventListener('click', this.navigationClickHandler);
    }
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    // Remove cleanup orchestration event listeners
    if (this.cleanupEventHandlers) {
      window.removeEventListener('beforeunload', this.cleanupEventHandlers.beforeUnload);
      window.removeEventListener('pagehide', this.cleanupEventHandlers.pageHide);
      document.removeEventListener('visibilitychange', this.cleanupEventHandlers.visibilityChange);
      window.removeEventListener('popstate', this.cleanupEventHandlers.popState);
      window.removeEventListener('hashchange', this.cleanupEventHandlers.hashChange);
      window.removeEventListener('unload', this.cleanupEventHandlers.unload);
    }

    // Cleanup core components
    if (this.userContextService && typeof this.userContextService.destroy === 'function') {
      this.userContextService.destroy();
    }

    // Clear global references
    this.userContextService = null;
    this.initialized = false;

    console.log('✅ ZubduqApp: Global resources cleaned up');
  }
}

// Create global app instance for cleanup orchestration
let globalApp = null;

// Initialize the application when DOM is loaded
document.addEventListener('DOMContentLoaded', async () => {
  globalApp = new ZubduqApp();
  await globalApp.init();

  // Make cleanup orchestrator globally available
  window.zubduqApp = globalApp;
  window.cleanupOrchestrator = globalApp;

  console.log('🎯 ZubduqApp: Application fully initialized with cleanup orchestration');

  // Log current cleanup status
  console.log('📊 Cleanup Status:', globalApp.getCleanupStatus());
});

// Emergency cleanup functions for debugging
window.emergencyCleanup = () => {
  if (globalApp) {
    console.log('🚨 Emergency cleanup triggered');
    globalApp.performMasterCleanup('emergency');
  }
};

window.getCleanupStatus = () => {
  if (globalApp) {
    return globalApp.getCleanupStatus();
  }
  return { error: 'App not initialized' };
};

// Make components available globally for backward compatibility
window.UserBarManager = UserBarManager;
window.renderUserBar = renderUserBar;
window.ToastManager = ToastManager;
window.showToast = showToast;
window.CSRFManager = CSRFManager;

// UserContextService is already made global in initializeUserContextService method

// Make auth services available globally
window.publicAuthService = publicAuthService;
window.AuthGuard = AuthGuard;

// Make analytics services available globally
window.analytics = analyticsService;
window.cookieConsent = cookieConsent;
